import * as React from "react";
import {
  ISharedAppContext,
  ISharedAppContextV2,
  SharedAppConsumer,
  SharedAppContextV2Consumer,
  SharedAppContextV2Provider,
  SharedAppProvider
} from "@origin-digital/shared-app-context";
import { LiveChat } from "../LiveChat";
import { Header } from "../Header";
import { IMenuItem } from "../Navigation/IMenuItem";
import {
  decodeOriginCustomerIdFromJwt,
  isLocalEnvironment
} from "../../auth/jwtUtils";
import { NavigationHeader } from "../NavigationHeader";
import { fetchAccessToken } from "@origin-digital/event-dispatcher";
import { EnvironmentNames } from "@origin-digital/platform-enums";
import {
  ExecutionEnvironment,
  getFeatureLoader,
  isFeatureEnabled,
  setupFeatures
} from "@origin-digital/features";

const FEATURE_TOGGLE_SAC_V2 = "sales-lpg-in-myaccount";

interface ISacVersionToggleProps {
  initSAC: boolean;
  liveChat?: Window["oetal"]["pageData"]["liveChat"];
  showHeader: boolean;
  authenticated: boolean;
  nav: IMenuItem[];
  actionBar: any;
  title: string;
}

interface ISacVersionToggleState {
  isSacV2User: boolean;
  originCustomerId?: string;
}

export class SacVersionToggle extends React.Component<
  ISacVersionToggleProps,
  ISacVersionToggleState
> {
  constructor(props) {
    super(props);
    this.state = {
      isSacV2User: false
    };
  }

  async componentDidMount() {
    // sac v2 and accompanying feature switch is only attempted for authenticated apps
    if (this.props.authenticated) {
      if (typeof window !== "undefined" && !isLocalEnvironment()) {
        let jwt;
        try {
          jwt = await fetchAccessToken({
            environment: window.oetal.env.name as EnvironmentNames
          });
        } catch (error) {
          // ignore and settle with standard sac v1 case. dont even bother setting up feature toggle
          return;
        }
        if (jwt) {
          const originCustomerId = decodeOriginCustomerIdFromJwt(
            jwt.accessToken
          );
          this.setState({ originCustomerId });
        }
      }

      setupFeatures({
        environment: window.oetal.env.name as ExecutionEnvironment,
        defaultFeatureValue: false,
        localMode: isLocalEnvironment()
          ? {
              features: {
                [FEATURE_TOGGLE_SAC_V2]: false
              }
            }
          : undefined
      });
      getFeatureLoader().addFeatureListener(() => {
        const isSacV2User = isFeatureEnabled(FEATURE_TOGGLE_SAC_V2, {
          originCustomerId: this.state.originCustomerId
        });
        this.setState({ isSacV2User });
      });
    }
  }

  render() {
    const { isSacV2User } = this.state;
    const {
      initSAC,
      liveChat,
      showHeader,
      authenticated,
      nav,
      actionBar,
      title
    } = this.props;

    return (
      <>
        {!isSacV2User && (
          <SharedAppProvider
            xhrHost={__DEV__ ? "http://localhost:4488" : undefined}
            xhrInitialize={initSAC}
          >
            <SharedAppConsumer>
              {(context: ISharedAppContext) => {
                const { customerCode, premiseId, properties } = context || {};
                return (
                  <>
                    <LiveChat customerCode={customerCode} liveChat={liveChat} />
                    <NavigationHeader
                      showHeader={showHeader}
                      authenticated={authenticated}
                      customerCode={customerCode}
                      nav={nav}
                    />
                    <Header
                      title={actionBar.title || title}
                      component={actionBar.component}
                      hideActionBar={actionBar.hidden}
                      mobilePropertySelector={actionBar.mobilePropertySelector}
                      mobileNavButton={actionBar.mobileNavButton}
                      authenticated={authenticated}
                      onSelectPremise={premiseId =>
                        context.actions.setPremiseId(premiseId)
                      }
                      premiseId={premiseId}
                      premises={properties}
                    />
                  </>
                );
              }}
            </SharedAppConsumer>
          </SharedAppProvider>
        )}
        {isSacV2User && (
          <SharedAppContextV2Provider
            xhrHost={__DEV__ ? "http://localhost:4488" : undefined}
            xhrInitialize={initSAC}
          >
            <SharedAppContextV2Consumer>
              {(context: ISharedAppContextV2) => {
                const { customerCode, premiseId, properties } = context || {};
                return (
                  <>
                    <LiveChat customerCode={customerCode} liveChat={liveChat} />
                    <NavigationHeader
                      showHeader={showHeader}
                      authenticated={authenticated}
                      customerCode={customerCode}
                      nav={nav}
                    />
                    <Header
                      title={actionBar.title || title}
                      component={actionBar.component}
                      hideActionBar={actionBar.hidden}
                      mobilePropertySelector={actionBar.mobilePropertySelector}
                      mobileNavButton={actionBar.mobileNavButton}
                      authenticated={authenticated}
                      onSelectPremise={premiseId =>
                        context.actions.setPremiseId(premiseId)
                      }
                      premiseId={premiseId}
                      premises={properties}
                    />
                  </>
                );
              }}
            </SharedAppContextV2Consumer>
          </SharedAppContextV2Provider>
        )}
      </>
    );
  }
}
